import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import AuthService from "../../../api/services/AuthService";
import WalletOverviewSkeleton from "../../../customComponents/WalletOverviewSkeleton";
import { ProfileContext } from "../../../context/ProfileProvider";
import "../FundingAccountPage/fundingAccount.css";
import "./assetOverview.css";
import { getWalletTypeLabel } from "../../../utils/walletTypeLabels";
import WalletTransferModals from "../WalletPage/WalletTransferModals";
import { useWalletTransfer } from "../WalletPage/useWalletTransfer";
import { USER_WALLET_REFRESH } from "../Order/depositDeclarationModel";

const ACCOUNT_ROW_CLASS = {
  spot: "spot",
  swap: "swap",
  earning: "staking",
  futures: "futures",
};

/** Unwrap `GET /v1/wallet/all-wallets-portfolio` payload. */
function parseAllWalletsPortfolioData(result) {
  let data = result?.data;
  if (data && typeof data === "object" && !Array.isArray(data) && data.data && typeof data.data === "object") {
    data = data.data;
  }
  return data && typeof data === "object" ? data : null;
}

/** Index wallets by `type` — supports array (`data.wallets[]`) or legacy map/list shapes. */
function normalizeWalletsFromPortfolio(data) {
  const wallets = {};
  const list = [];

  if (Array.isArray(data?.wallets)) {
    for (const w of data.wallets) {
      const key = String(w?.type ?? w?.wallet_type ?? w?.walletType ?? "").trim().toLowerCase();
      if (!key) continue;
      wallets[key] = w;
      list.push({ ...w, type: key });
    }
  } else if (data?.wallets && typeof data.wallets === "object") {
    for (const [k, v] of Object.entries(data.wallets)) {
      const key = String(v?.type ?? k).trim().toLowerCase();
      if (!key) continue;
      const row = { ...v, type: key };
      wallets[key] = row;
      list.push(row);
    }
  } else if (Array.isArray(data?.wallet_types)) {
    for (const wt of data.wallet_types) {
      const key = String(wt?.wallet_type ?? wt?.walletType ?? wt?.type ?? "").trim().toLowerCase();
      if (!key) continue;
      const row = { ...wt, type: key };
      wallets[key] = row;
      list.push(row);
    }
  } else if (Array.isArray(data?.walletTypes)) {
    for (const wt of data.walletTypes) {
      const key = String(wt?.wallet_type ?? wt?.walletType ?? wt?.type ?? "").trim().toLowerCase();
      if (!key) continue;
      const row = { ...wt, type: key };
      wallets[key] = row;
      list.push(row);
    }
  }

  return { wallets, list };
}

function walletAmountsFromPortfolioEntry(w, prefCurrency, toNum) {
  if (!w || typeof w !== "object") {
    return { dollarPrice: 0, currencyPrice: 0, Currency: prefCurrency };
  }
  const usd = toNum(
    w.estimated_usdt ??
    w.estimatedUsdt ??
    w.dollarPrice ??
    w.dollar_price ??
    w.amount_usd ??
    w.usd_amount ??
    w.usdAmount ??
    w.usdt ??
    w.usdt_amount ??
    0
  );
  const pref = toNum(
    w.estimated_preferred ??
    w.estimatedPreferred ??
    w.currencyPrice ??
    w.currency_price ??
    w.amount ??
    w.Amount ??
    w.balance ??
    w.total ??
    w.total_amount ??
    w.totalAmount ??
    0
  );
  return {
    dollarPrice: usd,
    currencyPrice: pref,
    Currency: prefCurrency,
  };
}

const AGCE_ACCOUNT_DONUT_COLORS = ["#f3bb2c", "#22c55e", "#3b82f6", "#a855f7", "#ef4444", "#14b8a6"];

function AgceDonutChart({ segments, size = 112, stroke = 12, emptyColor = "#334155", trackColor = "transparent", ariaLabel }) {
  const safeSegs = Array.isArray(segments) ? segments.filter((s) => (Number(s?.value) || 0) > 0) : [];
  const total = safeSegs.reduce((acc, s) => acc + (Number(s.value) || 0), 0);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;

  let offset = 0;
  return (
    <svg
      className="agce-ao-donut"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel || "Account allocation chart"}
    >
      <defs>
        <filter id="agce-ao-donut-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="1.3" floodColor="#000000" floodOpacity="0.35" />
          <feDropShadow dx="0" dy="6" stdDeviation="4.5" floodColor="#000000" floodOpacity="0.25" />
        </filter>
        <radialGradient id="agce-ao-donut-highlight" cx="30%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22" />
          <stop offset="55%" stopColor="#ffffff" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      <g filter="url(#agce-ao-donut-shadow)">
        {total > 0 ? (
          safeSegs.map((s) => {
            const value = Number(s.value) || 0;
            const frac = value / total;
            const dash = c * frac;
            const node = (
              <circle
                key={s.key}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={s.color || emptyColor}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${cx} ${cy})`}
              >
                {s.title ? <title>{s.title}</title> : null}
              </circle>
            );
            offset += dash;
            return node;
          })
        ) : (
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={emptyColor} strokeWidth={stroke} />
        )}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="url(#agce-ao-donut-highlight)" strokeWidth={stroke} />
      </g>
    </svg>
  );
}

const AssetOverview = () => {
  const navigate = useNavigate();
  const {
    estimatedPortfolio: contextEstimatedPortfolio,
    fetchEstimatedPortfolio,
    refreshEstimatedPortfolio,
  } = useContext(ProfileContext);

  const [fundData, setFundData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  const [showBalance, setShowBalance] = useState(true);
  const [activeTab, setActiveTab] = useState("assets"); // assets | account
  const [hideAssets, setHideAssets] = useState(false);
  const [search, setSearch] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const searchInputRef = useRef(null);
  /** Full estimated-portfolio response for Account tab only (`walletType=""`) — avoids shared context being overwritten. */
  const [accountTabPortfolio, setAccountTabPortfolio] = useState(null);
  /** Mobile: full row shown in popup ({ type: 'crypto', item } | { type: 'account', ... }) */
  const [assetOverviewRowModal, setAssetOverviewRowModal] = useState(null);
  const [overviewInitialLoadDone, setOverviewInitialLoadDone] = useState(false);

  const toggleSearch = useCallback(() => {
    setSearchActive((v) => !v);
  }, []);

  useEffect(() => {
    if (!searchActive) return;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 320);
    return () => window.clearTimeout(t);
  }, [searchActive]);

  useEffect(() => {
    if (!searchActive) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSearchActive(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [searchActive]);

  useEffect(() => {
    if (activeTab === "account") setSearchActive(false);
  }, [activeTab]);

  useEffect(() => {
    if (!assetOverviewRowModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setAssetOverviewRowModal(null);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [assetOverviewRowModal]);

  const safeRound = useCallback((value, decimals = 8) => {
    const num = parseFloat(value);
    if (Number.isNaN(num) || num <= 0) return "0.00";
    return parseFloat(num.toFixed(decimals));
  }, []);

  const toNum = useCallback((v) => {
    if (v === undefined || v === null) return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    const s = String(v).trim();
    if (!s) return 0;
    const cleaned = s.replace(/,/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }, []);

  /** Estimated balance header — avoid treating missing API fields as 0.00 */
  const formatEstimateHeader = useCallback((value, decimals = 2) => {
    if (value === undefined || value === null || value === "") return "—";
    const num = parseFloat(value);
    if (Number.isNaN(num)) return "—";
    return parseFloat(num.toFixed(decimals));
  }, []);

  const formatAccountTabUsd = useCallback((v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0.00";
    return n.toFixed(2);
  }, []);

  const formatAccountTabPreferred = useCallback((v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0.00";
    if (n === 0) return "0.00";
    return String(parseFloat(n.toFixed(8)));
  }, []);

  const portfolioUsdtEstimate = useCallback((p) => {
    if (!p || typeof p !== "object") return undefined;
    return (
      p.estimated_total_usdt ??
      p.dollarPrice ??
      p.estimatedTotalUsdt ??
      p.estimated_total ??
      p.total_usdt
    );
  }, []);

  const portfolioPreferredAmount = useCallback(
    (p) => {
      const usdt = portfolioUsdtEstimate(p);
      const pref = p?.estimated_total_preferred ?? p?.estimatedTotalPreferred;
      return pref !== undefined && pref !== null && pref !== "" ? pref : usdt;
    },
    [portfolioUsdtEstimate]
  );

  const portfolioPreferredCurrency = useCallback((p) => {
    if (!p || typeof p !== "object") return "USD";
    return (
      p.currency_prefrence ??
      p.currency_preference ??
      p.preferred_currency ??
      p.Currency ??
      "USD"
    );
  }, []);

  const totalWalletQty = useCallback((item) => {
    return (parseFloat(item?.balance) || 0) + (parseFloat(item?.locked_balance) || 0);
  }, []);

  const approxUsdDisplay = useCallback(
    (item) => {
      const raw = item?.dollar_price ?? item?.dollarPrice ?? item?.usd_value ?? item?.estimated_usd;
      const n = parseFloat(raw);
      if (!Number.isNaN(n) && n >= 0) return safeRound(n, 2);
      const sym = (item?.short_name || "").toUpperCase();
      const stable = ["USDT", "USDC", "BUSD", "DAI", "TUSD", "USDP"].includes(sym);
      const t = totalWalletQty(item);
      if (stable && t >= 0) return safeRound(t, 2);
      return null;
    },
    [safeRound, totalWalletQty]
  );

  const usdApproxFromPrice = useCallback(
    (amount, item) => {
      const sym = String(item?.short_name || "").trim().toUpperCase();
      const cur = String(item?.currency || "").trim().toUpperCase();
      if (sym === "USDT" || cur === "USDT") {
        const amt = parseFloat(amount) || 0;
        return `≈ $${safeRound(amt, 2)}`;
      }
      const raw = item?.price;
      const rawStr =
        raw === undefined || raw === null ? "" : String(raw).trim();
      if (rawStr === "" || rawStr.toUpperCase() === "N/A") {
        return "N/A";
      }
      const p = parseFloat(rawStr);
      if (Number.isNaN(p) || p <= 0) {
        return "N/A";
      }
      const amt = parseFloat(amount) || 0;
      return `≈ $${safeRound(amt * p, 2)}`;
    },
    [safeRound]
  );

  const approxUsdLine = useCallback(
    (item) => {
      const v = approxUsdDisplay(item);
      if (v != null) return `≈ $${v}`;
      const fromPrice = usdApproxFromPrice(totalWalletQty(item), item);
      return fromPrice !== "N/A" ? fromPrice : "≈ —";
    },
    [approxUsdDisplay, usdApproxFromPrice, totalWalletQty]
  );

  const spotUsdPriceLabel = useCallback(
    (item) => {
      const rawStr =
        item?.price === undefined || item?.price === null ? "" : String(item.price).trim();
      if (rawStr !== "" && rawStr.toUpperCase() !== "N/A") {
        const p = parseFloat(rawStr);
        if (!Number.isNaN(p) && p > 0) {
          return `$${safeRound(p, 5)}`;
        }
      }
      const sym = String(item?.short_name || "").trim().toUpperCase();
      const cur = String(item?.currency || "").trim().toUpperCase();
      if (sym === "USDT" || cur === "USDT") {
        return `$${safeRound(1, 2)}`;
      }
      return "N/A";
    },
    [safeRound]
  );

  const handleUserFunds = useCallback(async (type) => {
    try {
      const result = await AuthService.getUserfunds(type);
      if (result?.success) {
        const wallets = result?.data || [];
        setFundData(wallets);
        setCurrencyData((prev) => (prev && prev.length ? prev : wallets));
        return wallets;
      }
    } catch {
      // silent
    }
    return null;
  }, []);

  const loadAccountTabPortfolio = useCallback(async () => {
    try {
      const result = await AuthService.allWalletsPortfolio();
      if (result?.success) {
        setAccountTabPortfolio(parseAllWalletsPortfolioData(result));
      } else {
        setAccountTabPortfolio(null);
      }
    } catch {
      setAccountTabPortfolio(null);
    }
  }, []);

  const walletTransfer = useWalletTransfer({
    initialFrom: "spot",
    initialTo: "main",
    onTransferSuccess: async () => {
      await handleUserFunds("");
      await refreshEstimatedPortfolio?.("");
      await loadAccountTabPortfolio();
    },
  });

  useEffect(() => {
    void (async () => {
      try {
        setOverviewInitialLoadDone(false);
        await handleUserFunds("");
        await fetchEstimatedPortfolio?.("");
        await loadAccountTabPortfolio();
      } finally {
        setOverviewInitialLoadDone(true);
      }
    })();
  }, [handleUserFunds, fetchEstimatedPortfolio, loadAccountTabPortfolio]);

  useEffect(() => {
    const onWalletRefresh = () => {
      void handleUserFunds("");
    };
    window.addEventListener(USER_WALLET_REFRESH, onWalletRefresh);
    return () => window.removeEventListener(USER_WALLET_REFRESH, onWalletRefresh);
  }, [handleUserFunds]);

  const sortedFundData = useMemo(() => {
    const data = [...(fundData || [])];
    const n = (v) => {
      const x = Number(v);
      return Number.isFinite(x) ? x : 0;
    };
    return data.sort((a, b) => {
      const totalA = n(a?.balance) + n(a?.locked_balance) + n(a?.bonus);
      const totalB = n(b?.balance) + n(b?.locked_balance) + n(b?.bonus);
      if (totalA > 0 && totalB === 0) return -1;
      if (totalA === 0 && totalB > 0) return 1;
      return totalB - totalA;
    });
  }, [fundData]);

  const filteredCoinList = useMemo(() => {
    const s = search.trim().toLowerCase();
    let rows = sortedFundData;
    if (s) {
      rows = rows.filter(
        (item) => item?.short_name?.toLowerCase?.().includes(s) || item?.currency?.toLowerCase?.().includes(s)
      );
    }
    if (hideAssets) {
      rows = rows.filter((item) => (Number(item?.balance) || 0) + (Number(item?.locked_balance) || 0) > 0);
    }
    return rows;
  }, [sortedFundData, search, hideAssets]);

  /** Account tab: `GET all-wallets-portfolio` → `data.wallets[]` with `estimated_usdt` / `estimated_preferred`. */
  const accountOverviewDerived = useMemo(() => {
    const data = accountTabPortfolio;
    const { wallets, list } = normalizeWalletsFromPortfolio(data);
    const prefCurrency =
      data?.currency_prefrence ??
      data?.currency_preference ??
      data?.preferred_currency ??
      portfolioPreferredCurrency(contextEstimatedPortfolio || {}) ??
      "USD";

    const accountRows = list.map((w) => {
      const key = String(w.type).trim().toLowerCase();
      return {
        key,
        label: w.label || getWalletTypeLabel(key),
        className: ACCOUNT_ROW_CLASS[key] || "",
      };
    });

    const balances = {};
    for (const { key } of accountRows) {
      balances[key] = walletAmountsFromPortfolioEntry(wallets[key], prefCurrency, toNum);
    }

    const totalUsd = toNum(
      data?.estimated_total_usdt ??
      data?.estimatedTotalUsdt ??
      accountRows.reduce((sum, { key }) => sum + toNum(balances[key]?.dollarPrice), 0)
    );
    const totalPreferred = toNum(
      data?.estimated_total_preferred ??
      data?.estimatedTotalPreferred ??
      accountRows.reduce((sum, { key }) => sum + toNum(balances[key]?.currencyPrice), 0)
    );

    return { balances, totalUsd, totalPreferred, prefCurrency, accountRows };
  }, [accountTabPortfolio, contextEstimatedPortfolio, portfolioPreferredCurrency, toNum]);

  const accountDonutSegments = useMemo(() => {
    if (activeTab !== "account") return [];
    if (!showBalance) return [];
    const segs = accountOverviewDerived.accountRows.map((r, idx) => {
      const bal = accountOverviewDerived.balances[r.key];
      // Use the exact "Amount" value (preferred). If backend doesn't provide it, fallback to USD so chart still reflects balances.
      const amount = toNum(bal?.currencyPrice) || toNum(bal?.dollarPrice);
      if (!(amount > 0)) return null;
      return {
        key: r.key,
        label: r.label,
        value: amount,
        color: AGCE_ACCOUNT_DONUT_COLORS[idx % AGCE_ACCOUNT_DONUT_COLORS.length],
        usdText: `$${formatAccountTabUsd(Number(bal?.dollarPrice) || 0)}`,
        prefText: `${formatAccountTabPreferred(Number(bal?.currencyPrice) || 0)} ${bal?.Currency || accountOverviewDerived.prefCurrency}`,
      };
    }).filter(Boolean);
    const total = segs.reduce((a, s) => a + toNum(s.value), 0);
    if (!(total > 0)) return [];
    return segs.map((s) => {
      const pct = ((toNum(s.value) / total) * 100).toFixed(2);
      return { ...s, pct, title: `${s.label}: ${pct}%` };
    });
  }, [activeTab, showBalance, accountOverviewDerived, formatAccountTabPreferred, formatAccountTabUsd, toNum]);

  const openTransferModalForRow = (item) => {
    walletTransfer.openTransferModal({ from: "spot", to: "main", currency: item });
  };

  /** Mobile row sheet unmounts the trigger before Bootstrap can open the modal; open via jQuery after paint. */
  const showWalletTransferModalImperative = () => {
    if (typeof window === "undefined") return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          window.$?.("#walletTransferModal")?.modal?.("show");
        } catch {
          // Bootstrap/jQuery not available
        }
      });
    });
  };

  useEffect(() => {
    setAssetOverviewRowModal((prev) => {
      if (!prev || prev.type !== "crypto" || !prev.item?.currency_id) return prev;
      const fresh = fundData?.find((r) => String(r.currency_id) === String(prev.item.currency_id));
      if (!fresh) return prev;
      const pb = Number(prev.item.balance),
        pl = Number(prev.item.locked_balance),
        fb = Number(fresh.balance),
        fl = Number(fresh.locked_balance);
      if (pb === fb && pl === fl) return prev;
      return { ...prev, item: { ...prev.item, ...fresh } };
    });
  }, [fundData]);

  useEffect(() => {
    setAssetOverviewRowModal((prev) => {
      if (!prev || prev.type !== "account" || !prev.accountKey) return prev;
      const bal = accountOverviewDerived.balances[prev.accountKey];
      if (!bal) return prev;
      const total = accountOverviewDerived.totalUsd;
      const ratioNum = total > 0 ? (Number(bal.dollarPrice) / total) * 100 : 0;
      const ratioPct = `${ratioNum.toFixed(2)}%`;
      if (
        Number(prev.dollarPrice) === Number(bal.dollarPrice) &&
        Number(prev.currencyPrice) === Number(bal.currencyPrice) &&
        prev.ratio === ratioPct
      ) {
        return prev;
      }
      return {
        ...prev,
        currencyPrice: bal.currencyPrice,
        dollarPrice: bal.dollarPrice,
        Currency: bal.Currency ?? prev.Currency,
        amountPrimary: `${formatAccountTabPreferred(bal.currencyPrice)} ${bal.Currency ?? accountOverviewDerived.prefCurrency}`,
        amountUsd: `$${formatAccountTabUsd(bal.dollarPrice)}`,
        ratio: ratioPct,
      };
    });
  }, [accountOverviewDerived, formatAccountTabPreferred, formatAccountTabUsd]);

  return (
    <>
      {overviewInitialLoadDone ? (
        <>
          <div className="dashboard_right">


            <div className="dashboard_listing_section Overview_mid padding-left">
              <div className="assets_wallets_section">

                <div className="row">
                  <div className="col-sm-12">
                    <div className="overview_section">

                      <div className="funding-account__header border-0 pb-0">

                        <div><h1 className="funding-account__title">Assets Overview</h1></div>

                        <div className="d-flex gap-2 mt-3 mt-md-0">
                          <button className="btn btn-deposit px-4" onClick={() => navigate("/asset_managemnet/deposit")}>
                            Deposit
                          </button>
                          <button className="btn btn-outline-custom px-4" onClick={() => navigate("/asset_managemnet/withdraw")}>
                            Withdraw
                          </button>
                          <button
                            className="btn btn-outline-custom px-4"
                            data-bs-toggle="modal"
                            data-bs-target="#walletTransferModal"
                            onClick={(e) => {
                              e.preventDefault();
                              walletTransfer.openTransferModal({
                                from: "spot",
                                to: "main",
                                currency: fundData[0] || {},
                              });
                            }}
                          >
                            Transfer
                          </button>
                        </div>
                      </div>

                      <div className="estimated_balance">

                        <div className="wallet-header d-flex flex-wrap align-items-center justify-content-between">
                          <div>
                            <h6>
                              Estimated Balance
                              <button type="button" onClick={() => setShowBalance((v) => !v)}>
                                <i className={showBalance ? "ri-eye-line" : "ri-eye-off-line"}></i>
                              </button>
                            </h6>
                            <div className="wallet-title">
                              {showBalance
                                ? formatEstimateHeader(portfolioPreferredAmount(contextEstimatedPortfolio), 5)
                                : "****"}{" "}
                              {portfolioPreferredCurrency(contextEstimatedPortfolio)}
                            </div>
                            <div className="wallet-sub mt-1">
                              ≈{" "}
                              {showBalance ? formatEstimateHeader(portfolioUsdtEstimate(contextEstimatedPortfolio), 5) : "****"}{" "}
                              {contextEstimatedPortfolio?.Currency || "USD"}
                              <span onClick={() => navigate("/asset_managemnet/deposit")} className="cursor-pointer">
                                Deposit crypto instantly with one-click <i className="ri-arrow-right-s-line"></i>
                              </span>
                            </div>
                          </div>
                          {activeTab === "account" ? (
                            <div className="agce-ao-est-donutwrap" aria-hidden={!showBalance}>
                              <div className="agce-ao-est-donut">
                                <AgceDonutChart segments={accountDonutSegments} ariaLabel="Account allocation donut chart" />
                              </div>
                              <div className="agce-ao-donut-legend" aria-label="Account allocation legend">
                                {accountDonutSegments.length ? (
                                  accountDonutSegments.map((s) => (
                                    <div key={s.key} className="agce-ao-donut-legend__row" title={`${s.label} • ${s.pct}%`}>
                                      <span className="agce-ao-donut-legend__dot" style={{ background: s.color }} aria-hidden="true" />
                                      <span className="agce-ao-donut-legend__label">{s.label}</span>
                                      <span className="agce-ao-donut-legend__value">{s.pct}%</span>
                                    </div>
                                  ))
                                ) : (
                                  <div className="agce-ao-donut-legend__empty">No balances</div>
                                )}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-12">
                    <div className="market_section">
                      <div className="coin_view_top">
                        <div className="wallet_tabs">
                          <button className={activeTab === "assets" ? "tab_btn active" : "tab_btn"} onClick={() => setActiveTab("assets")}>
                            Crypto
                          </button>
                          <button
                            className={activeTab === "account" ? "tab_btn active" : "tab_btn"}
                            onClick={() => {
                              setActiveTab("account");
                              void loadAccountTabPortfolio();
                            }}
                          >
                            Account
                          </button>
                        </div>

                        {activeTab !== "account" ? (
                          <div className="coin_right">
                            <div className={`searchBar custom-tabs${searchActive ? " search_active" : ""}`}>
                              <button
                                type="button"
                                className="agce-assets-search__trigger"
                                aria-expanded={searchActive}
                                aria-controls="asset-overview-crypto-search"
                                onClick={toggleSearch}
                              >
                                <i className="ri-search-2-line" aria-hidden="true" />
                                <span className="visually-hidden">Search crypto</span>
                              </button>
                              <input
                                id="asset-overview-crypto-search"
                                ref={searchInputRef}
                                type="search"
                                className="custom_search"
                                placeholder="Search Crypto"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                aria-label="Search crypto"
                              />
                            </div>
                            <div className="checkbox">
                              <input type="checkbox" checked={hideAssets} onChange={() => setHideAssets((v) => !v)} />
                              Hide 0 Balance
                            </div>
                          </div>
                        ) : null}
                      </div>

                      {activeTab === "assets" ? (
                        <div className="dashboard_summary">
                          <div className="desktop_view-2">
                            <div className="funding-account__tablewrap table-responsive">
                              <table className="funding-account__table">
                                <thead>
                                  <tr>
                                    <th>Coin</th>
                                    <th className="ta-r2">Total Amount</th>
                                    <th className="ta-r">Available</th>
                                    <th className="ta-r">In Order</th>
                                    <th className="ta-r">Avg. Cost Price</th>
                                    <th className="ta-r">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredCoinList?.length ? (
                                    filteredCoinList.map((item, index) => (
                                      <tr key={item?.currency_id || index}>
                                        <td data-label="Coin">
                                          <div className="funding-account__coin flex-grow-1">
                                            <img
                                              className="funding-account__coin-img"
                                              src={
                                                item?.icon_path ? resolvePublicUploadUrl(item.icon_path) : "/images/AnnouncementImg/activities_icon.png"
                                              }
                                              alt=""
                                              onError={(e) => {
                                                e.target.src = "/images/AnnouncementImg/activities_icon.png";
                                              }}
                                            />
                                            <div>
                                              <div className="funding-account__coin-sym">{item?.short_name}</div>
                                              <div className="funding-account__coin-name">{item?.currency}</div>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="ta-r agce-ao-total-balance-cell asstes_tdfirst" data-label="Total Amount">
                                          <div className="d-flex justify-content-start gap-2 agce-ao-mobile-total-with-more">
                                            <div className="agce-ao-balance-stack funding-account__num-stack">
                                              <div className="funding-account__num agce-ao-balance-stack__main">
                                                {safeRound(totalWalletQty(item))}
                                              </div>
                                              <div className="funding-account__subnum">{approxUsdLine(item)}</div>
                                            </div>
                                            <button
                                              type="button"
                                              className="agce-ao-more-btn d-inline-flex d-lg-none flex-shrink-0"
                                              aria-label="View full row"
                                              onClick={() => setAssetOverviewRowModal({ type: "crypto", item })}
                                            >
                                              <i className="ri-more-2-fill" aria-hidden="true" />
                                            </button>
                                          </div>
                                        </td>
                                        <td className="ta-r" data-label="Available">
                                          <div className="funding-account__num">{safeRound(item?.balance)}</div>
                                          <div className="funding-account__subnum">{usdApproxFromPrice(item?.balance, item)}</div>
                                        </td>
                                        <td className="ta-r" data-label="In Order">
                                          <div className="funding-account__num">{safeRound(item?.locked_balance)}</div>
                                          <div className="funding-account__subnum">
                                            {usdApproxFromPrice(item?.locked_balance, item)}
                                          </div>
                                        </td>
                                        <td className="ta-r" data-label="Avg. Cost Price">
                                          <div className="funding-account__num">{spotUsdPriceLabel(item)}</div>
                                        </td>
                                        <td className="ta-r" data-label="Action">
                                          <div className="funding-account__actions">
                                            <a
                                              href="#/"
                                              className="funding-account__action"
                                              data-bs-toggle="modal"
                                              data-bs-target="#walletTransferModal"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                openTransferModalForRow(item);
                                              }}
                                            >
                                              Transfer
                                            </a>
                                            <Link to={`/trade/${item?.short_name}_USDT`} className="funding-account__action">
                                              Trade
                                            </Link>
                                          </div>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr className="no-data-row2">
                                      <td colSpan="6">
                                        <div className="no-data-wrapper">
                                          <div className="no_data_vector">
                                            <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                                            <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data" />
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {activeTab === "account" ? (
                        <div className="dashboard_summary dummy_tab account_table">
                          <div className="d-flex gap-3 justify-content-between account_tabcnt">
                            <div className="table-responsive">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Account</th>
                                    <th>Amount</th>
                                    <th>Ratio</th>
                                    <th className="right_td">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const total = accountOverviewDerived.totalUsd;
                                    return accountOverviewDerived.accountRows.map((a) => {
                                      const bal = accountOverviewDerived.balances[a.key] || {
                                        dollarPrice: 0,
                                        currencyPrice: 0,
                                        Currency: accountOverviewDerived.prefCurrency,
                                      };
                                      const ratio = total > 0 ? ((Number(bal.dollarPrice) / total) * 100).toFixed(2) : "0.00";
                                      return (
                                        <tr key={a.key}>
                                          <td>
                                            <div className={`td_first ${a.className}`}>{a.label}</div>
                                          </td>
                                          <td className="amount_td agce-ao-account-amount-cell">
                                            <div className="d-flex align-items-center justify-content-start gap-2 agce-ao-mobile-total-with-more">
                                              <div className="agce-ao-account-amount-stack">
                                                <span className="agce-ao-account-amount-stack__main">
                                                  {formatAccountTabPreferred(bal.currencyPrice)} {bal.Currency}
                                                </span>
                                                <span className="agce-ao-account-amount-stack__fiat">${formatAccountTabUsd(bal.dollarPrice)}</span>
                                              </div>
                                              <button
                                                type="button"
                                                className="agce-ao-more-btn d-inline-flex d-lg-none flex-shrink-0"
                                                aria-label="View full row"
                                                onClick={() =>
                                                  setAssetOverviewRowModal({
                                                    type: "account",
                                                    accountKey: a.key,
                                                    label: a.label,
                                                    amountPrimary: `${formatAccountTabPreferred(bal.currencyPrice)} ${bal.Currency}`,
                                                    amountUsd: `$${formatAccountTabUsd(bal.dollarPrice)}`,
                                                    ratio: `${ratio}%`,
                                                    currencyPrice: bal.currencyPrice,
                                                    dollarPrice: bal.dollarPrice,
                                                    Currency: bal.Currency,
                                                  })
                                                }
                                              >
                                                <i className="ri-more-2-fill" aria-hidden="true" />
                                              </button>
                                            </div>
                                          </td>
                                          <td>{ratio}%</td>
                                          <td className="right_td">
                                            <div className="d-flex gap-2 gap-md-3 justify-content-end align-items-center flex-wrap">
                                              <a
                                                href="#/"
                                                className="btn-link text-white"
                                                data-bs-toggle="modal"
                                                data-bs-target="#walletTransferModal"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  walletTransfer.openTransferModal({
                                                    from: a.key === "main" ? "main" : a.key,
                                                    to: a.key === "main" ? "spot" : "main",
                                                    currency: fundData[0] || {},
                                                  });
                                                }}
                                              >
                                                Transfer
                                              </a>
                                              {/* <Link to="/user_profile/wallet_transfer_History" className="btn-link text-white">
                                                History
                                              </Link> */}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    });
                                  })()}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {assetOverviewRowModal ? (
            <>
              <div className="agce-ao-detail-backdrop" role="presentation" onClick={() => setAssetOverviewRowModal(null)} />
              <div
                className="modal fade show d-flex agce-ao-sheet-modal"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                aria-labelledby="assetOverviewRowModalTitle"
                style={{ zIndex: 1060 }}
              >
                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable agce-ao-sheet-dialog">
                  <div className="modal-content agce-ao-sheet border-0">
                    {assetOverviewRowModal.type === "crypto" && assetOverviewRowModal.item ? (
                      <>
                        <h2 id="assetOverviewRowModalTitle" className="visually-hidden">
                          {assetOverviewRowModal.item?.short_name} details
                        </h2>
                        <div className="agce-ao-sheet__header">
                          <div className="agce-ao-sheet__brand">
                            <img
                              className="agce-ao-sheet__coin"
                              src={resolvePublicUploadUrl(assetOverviewRowModal.item?.icon_path)}
                              alt=""
                              onError={(e) => {
                                e.target.src = "/images/AnnouncementImg/activities_icon.png";
                              }}
                            />
                            <div>
                              <div className="agce-ao-sheet__sym">{assetOverviewRowModal.item.short_name}</div>
                              <div className="agce-ao-sheet__fullname">{assetOverviewRowModal.item.currency}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn-close agce-ao-sheet__close"
                            aria-label="Close"
                            onClick={() => setAssetOverviewRowModal(null)}
                          />
                        </div>
                        <div className="modal-body agce-ao-sheet__body">
                          {(() => {
                            const rowItem = assetOverviewRowModal.item;
                            const tot = totalWalletQty(rowItem);
                            return (
                              <>
                                <div className="agce-ao-sheet__hero">
                                  <div className="agce-ao-sheet__hero-num">{safeRound(tot)}</div>
                                  <div className="agce-ao-sheet__hero-fiat">{approxUsdLine(rowItem)}</div>
                                </div>
                                <div className="agce-ao-sheet__kvlist">
                                  <div className="agce-ao-sheet__kv">
                                    <span>Available</span>
                                    <span className="agce-ao-sheet__kv-val text-end">
                                      <span className="d-block">{safeRound(rowItem.balance)}</span>
                                      <span className="d-block funding-account__subnum">
                                        {usdApproxFromPrice(rowItem.balance, rowItem)}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="agce-ao-sheet__kv">
                                    <span>In-Order</span>
                                    <span className="agce-ao-sheet__kv-val text-end">
                                      <span className="d-block">{safeRound(rowItem.locked_balance)}</span>
                                      <span className="d-block funding-account__subnum">
                                        {usdApproxFromPrice(rowItem.locked_balance, rowItem)}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="agce-ao-sheet__kv">
                                    <span>Avg. Cost Price (USD)</span>
                                    <span className="agce-ao-sheet__kv-val text-end">{spotUsdPriceLabel(rowItem)}</span>
                                  </div>
                                  <div className="agce-ao-sheet__kv">
                                    <span>Total Balance</span>
                                    <span className="agce-ao-sheet__kv-val agce-ao-sheet__kv-val--accent">{safeRound(tot)}</span>
                                  </div>
                                </div>
                                <div className="agce-ao-sheet__divider" />
                                <div className="agce-ao-sheet__table-actions">
                                  <button
                                    type="button"
                                    className="agce-ao-sheet__pill agce-ao-sheet__pill--primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setAssetOverviewRowModal(null);
                                      openTransferModalForRow(rowItem);
                                      showWalletTransferModalImperative();
                                    }}
                                  >
                                    Transfer
                                  </button>
                                  <Link
                                    to={`/trade/${rowItem?.short_name}_USDT`}
                                    className="agce-ao-sheet__pill"
                                    onClick={() => setAssetOverviewRowModal(null)}
                                  >
                                    Trade
                                  </Link>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </>
                    ) : null}
                    {assetOverviewRowModal.type === "account" ? (
                      <>
                        <h2 id="assetOverviewRowModalTitle" className="visually-hidden">
                          {assetOverviewRowModal.label} account details
                        </h2>
                        <div className="agce-ao-sheet__header">
                          <div className="agce-ao-sheet__brand">
                            <div className="agce-ao-sheet__account-badge" aria-hidden="true">
                              {assetOverviewRowModal.label?.charAt(0) || "—"}
                            </div>
                            <div>
                              <div className="agce-ao-sheet__sym">{assetOverviewRowModal.label}</div>
                              <div className="agce-ao-sheet__fullname">Account</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn-close agce-ao-sheet__close"
                            aria-label="Close"
                            onClick={() => setAssetOverviewRowModal(null)}
                          />
                        </div>
                        <div className="modal-body agce-ao-sheet__body">
                          <div className="agce-ao-sheet__hero">
                            <div className="agce-ao-sheet__hero-num">${safeRound(assetOverviewRowModal.dollarPrice)}</div>
                            <div className="agce-ao-sheet__hero-fiat">
                              {safeRound(assetOverviewRowModal.currencyPrice)} {assetOverviewRowModal.Currency}
                            </div>
                          </div>
                          <div className="agce-ao-sheet__kvlist">
                            <div className="agce-ao-sheet__kv">
                              <span>Amount</span>
                              <span className="agce-ao-sheet__kv-val agce-ao-sheet__kv-val--accent">
                                {assetOverviewRowModal.amountPrimary}
                              </span>
                            </div>
                            <div className="agce-ao-sheet__kv">
                              <span>Estimated (USD)</span>
                              <span className="agce-ao-sheet__kv-val">{assetOverviewRowModal.amountUsd}</span>
                            </div>
                            <div className="agce-ao-sheet__kv">
                              <span>Ratio</span>
                              <span className="agce-ao-sheet__kv-val">{assetOverviewRowModal.ratio}</span>
                            </div>
                          </div>
                          <div className="agce-ao-sheet__divider" />
                          <div className="agce-ao-sheet__table-actions">
                            <button
                              type="button"
                              className="agce-ao-sheet__pill agce-ao-sheet__pill--primary"
                              onClick={(e) => {
                                e.preventDefault();
                                const key = assetOverviewRowModal.accountKey;
                                setAssetOverviewRowModal(null);
                                walletTransfer.openTransferModal({
                                  from: key === "main" ? "main" : key,
                                  to: key === "main" ? "spot" : "main",
                                  currency: fundData[0] || {},
                                });
                                showWalletTransferModalImperative();
                              }}
                            >
                              Transfer
                            </button>
                            <Link to="/user_profile/wallet_transfer_History" className="agce-ao-sheet__pill" onClick={() => setAssetOverviewRowModal(null)}>
                              History
                            </Link>
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          ) : null}

        </>
      ) : (
        <WalletOverviewSkeleton variant="asset-overview" />
      )}

      <WalletTransferModals
        fromWalletType={walletTransfer.fromWalletType}
        setFromWalletType={walletTransfer.setFromWalletType}
        toWalletType={walletTransfer.toWalletType}
        setToWalletType={walletTransfer.setToWalletType}
        availableWalletTypes={walletTransfer.availableWalletTypes}
        walletLabelsMap={walletTransfer.walletLabelsMap}
        selectedCurrency={walletTransfer.selectedCurrency}
        setSelectedCurrency={walletTransfer.setSelectedCurrency}
        transferAmount={walletTransfer.transferAmount}
        setTransferAmount={walletTransfer.setTransferAmount}
        transferable={walletTransfer.transferable}
        balanceLoading={walletTransfer.balanceLoading}
        sourceHint={walletTransfer.sourceHint}
        handleInterchangeWallet={walletTransfer.handleInterchangeWallet}
        handleTransfer={walletTransfer.handleTransfer}
        currencyData={walletTransfer.currencyData}
        fiveDecimal={walletTransfer.fiveDecimal}
        marginInvolved={walletTransfer.marginInvolved}
        marginPairs={walletTransfer.marginPairs}
        selectedMarginPair={walletTransfer.selectedMarginPair}
        setSelectedMarginPair={walletTransfer.setSelectedMarginPair}
        marginAssetType={walletTransfer.marginAssetType}
        setMarginAssetType={walletTransfer.setMarginAssetType}
        marginPairDropOpen={walletTransfer.marginPairDropOpen}
        setMarginPairDropOpen={walletTransfer.setMarginPairDropOpen}
      />
    </>
  );
};

export default AssetOverview;

